import { useEffect, useState } from 'react'
import { api } from '../api'
import { navigate } from '../router'
import type { Client, Invoice } from '../../shared/types'
import { formatCents } from '../../shared/money'
import { pushError, pushToast } from '../toast'
import { useColumns, ColGroup, ColResizer, ColumnsMenu, type ColumnDef } from '../columns'
import { mmddyy } from '../datetime'

const DEFS: ColumnDef[] = [
  { key: 'number', label: 'Number', width: 150 },
  { key: 'issued', label: 'Issued', width: 120 },
  { key: 'period', label: 'Period', width: 200 },
  { key: 'total', label: 'Total', width: 110 },
  { key: 'status', label: 'Status', width: 100 },
  { key: 'actions', label: '', width: 140, fixed: true },
]

export default function InvoiceList({ clientId }: { clientId: number }) {
  const [client, setClient] = useState<Client | null>(null)
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const colsApi = useColumns('invoices', DEFS)

  useEffect(() => {
    ;(async () => {
      try {
        const [{ client }, inv] = await Promise.all([api.getClient(clientId), api.listInvoices(clientId)])
        setClient(client)
        setInvoices(inv)
      } catch (e) {
        pushError(`Couldn't load invoices: ${(e as Error).message}`)
      } finally {
        setLoading(false)
      }
    })()
  }, [clientId])

  async function release(inv: Invoice) {
    const ok = confirm(
      `Release invoice ${inv.invoice_number}?\n\nIt will be deleted and its time entries become editable again. If it's the most recent invoice, its number is freed for reuse. This cannot be undone.`,
    )
    if (!ok) return
    try {
      await api.releaseInvoice(inv.id)
      setInvoices((is) => is.filter((i) => i.id !== inv.id))
      pushToast('Invoice released')
    } catch (e) {
      pushError(`Couldn't release: ${(e as Error).message}`)
    }
  }

  async function togglePaid(inv: Invoice) {
    const next = inv.status === 'paid' ? 'issued' : 'paid'
    setInvoices((is) => is.map((i) => (i.id === inv.id ? { ...i, status: next } : i)))
    try {
      await api.setInvoiceStatus(inv.id, next)
    } catch (e) {
      pushError(`Couldn't update status: ${(e as Error).message}`)
    }
  }

  const accent = client?.accent_color ?? '#3b6ea5'

  return (
    <div style={{ ['--accent' as any]: accent }} className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-10">
      <header className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button className="text-sm text-slate-400 hover:text-slate-700" onClick={() => navigate(`#/client/${clientId}`)}>
            ← {client?.simple_name ?? 'Client'}
          </button>
          <h1 className="text-xl font-semibold text-slate-900">Invoices</h1>
        </div>
        {invoices.length > 0 && <ColumnsMenu cols={colsApi} />}
      </header>

      {loading ? (
        <p className="text-slate-400">Loading…</p>
      ) : invoices.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 p-10 text-center text-slate-500">
          No invoices yet. Generate one from the tracker.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="border-collapse text-sm" style={{ tableLayout: 'fixed', width: colsApi.totalWidth }}>
            <ColGroup cols={colsApi} />
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                {colsApi.visible.map((d) =>
                  d.key === 'actions' ? (
                    <th key="actions" className="px-3 py-2" />
                  ) : (
                    <th key={d.key} className={'relative px-3 py-2 ' + (d.key === 'total' ? 'text-right' : '')}>
                      {d.label}
                      <ColResizer cols={colsApi} colKey={d.key} />
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50/40">
                  {colsApi.visible.map((d) => {
                    switch (d.key) {
                      case 'number':
                        return <td key="number" className="tabular px-3 py-2 font-medium text-slate-900">{inv.invoice_number}</td>
                      case 'issued':
                        return <td key="issued" className="tabular px-3 py-2 text-slate-600">{mmddyy(inv.issued_date)}</td>
                      case 'period':
                        return <td key="period" className="tabular px-3 py-2 text-slate-500">{mmddyy(inv.period_start)} – {mmddyy(inv.period_end)}</td>
                      case 'total':
                        return <td key="total" className="tabular px-3 py-2 text-right text-slate-900">{formatCents(inv.total_cents)}</td>
                      case 'status':
                        return (
                          <td key="status" className="px-3 py-2">
                            <button
                              onClick={() => togglePaid(inv)}
                              className={'rounded-full px-2 py-0.5 text-xs font-medium ' + (inv.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}
                              title="Toggle paid"
                            >
                              {inv.status}
                            </button>
                          </td>
                        )
                      case 'actions':
                        return (
                          <td key="actions" className="whitespace-nowrap px-3 py-2 text-right">
                            <button className="text-[var(--accent)] hover:underline" onClick={() => navigate(`#/invoice/${inv.id}`)}>
                              Open
                            </button>
                            <button className="ml-3 text-slate-400 hover:text-red-600" onClick={() => release(inv)} title="Release / delete">
                              Release
                            </button>
                          </td>
                        )
                      default:
                        return null
                    }
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
